'use client';

import { useEffect, useState } from 'react';
import { collection, doc, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { detallesDelDia } from '@/lib/programa-ss';
import { recorridoAMc, type Calib, type ProcesionRuta } from '@/components/cecop/ProcesionMap';
import type {
  CecopDia, CecopDiaHermandad, CecopDiaLive, CecopDiaLiveEntry, CecopDiaPaso, CecopLiveEntry,
  DiaSS, DiaSSHermandadDetalles, JartinetHermandad,
} from '@/lib/types';

/** Color de recorrido cuando la hermandad no tiene ninguno definido. */
export const COLOR_POR_DEFECTO = '#d4af37';

/**
 * Puestos de una hermandad en el día: la cruz de guía (que es la primera fila
 * de su cortejo) y un puesto por paso. `pasos` sale del programa ("1 PASO" /
 * "2 PASOS"), pero se conserva lo ya asignado si el número cambia.
 */
export function construirPuestos(
  detalle: DiaSSHermandadDetalles | undefined,
  prev: CecopDiaPaso[] = [],
): CecopDiaPaso[] {
  const n = detalle?.pasos === '2 PASOS' ? 2 : 1;
  const out: CecopDiaPaso[] = [];
  for (let i = 1; i <= n; i++) {
    const p = prev.find((x) => x.index === i);
    out.push({
      index: i,
      label: p?.label || (n === 2 ? (i === 1 ? 'Paso de Cristo' : 'Paso de palio') : 'Paso'),
      entityUuid: p?.entityUuid || '',
      entityName: p?.entityName || '',
    });
  }
  return out;
}

/**
 * La fila de una hermandad tal y como se guarda en `cecop_dias`, partiendo de
 * lo que ya hubiera guardado (así cambiar un paso en el programa no borra las
 * asignaciones que ya funcionaban).
 */
export function construirFilaHermandad(
  detalle: DiaSSHermandadDetalles,
  hermandad: JartinetHermandad | undefined,
  prev?: CecopDiaHermandad,
): CecopDiaHermandad {
  return {
    hermandadId: detalle.hermandadId,
    nombre: prev?.nombre || hermandad?.nombre || hermandad?.nombreCompleto || 'Hermandad',
    color: prev?.color || detalle.recorrido?.color || hermandad?.colorPrimario || COLOR_POR_DEFECTO,
    cortejoId: prev?.cortejoId ?? null,
    cortejoLabel: prev?.cortejoLabel || '',
    // El ajuste propio se conserva al sincronizar con el programa, y la clave NO se
    // escribe si no lo hay: `undefined` dentro de un array rompe la escritura.
    ...(prev?.ajuste ? { ajuste: prev.ajuste } : {}),
    pasos: construirPuestos(detalle, prev?.pasos),
  };
}

/**
 * Sincroniza la lista de hermandades del CECOP con las del día del programa:
 * añade las que falten, quita las que ya no salen y conserva lo asignado.
 */
export function sincronizarHermandades(
  cecop: CecopDia | null,
  dia: DiaSS | null,
  hermandades: JartinetHermandad[],
): CecopDiaHermandad[] {
  return detallesDelDia(dia).map((d) =>
    construirFilaHermandad(
      d,
      hermandades.find((h) => h.id === d.hermandadId),
      (cecop?.hermandades || []).find((x) => x.hermandadId === d.hermandadId),
    ),
  );
}

export interface MapaDelDia {
  /** Un recorrido por hermandad, ya en coordenadas de Minecraft. */
  rutas: ProcesionRuta[];
  /** Marcadores en vivo (escudo en círculo negro con filete de su color). */
  entries: CecopLiveEntry[];
}

/**
 * Todo lo que hay que pintar de un día: los recorridos de sus cofradías y los
 * marcadores en vivo.
 *
 * **Los recorridos salen de `dias_ss` y NO se copian** al documento del CECOP:
 * así retocar un trazado en el programa se ve al momento en el GPS, y el panel
 * público (que ya lee `dias_ss`, de lectura pública) no necesita una segunda
 * copia que mantener. Lo que sí se copia es el MAPA (imagen + calibración),
 * porque `mod_map_config` es solo-admin.
 *
 * Los puntos del programa van en % de la imagen y aquí se pasan a coordenadas
 * de Minecraft con esa misma calibración, que es donde vive el GPS.
 */
export function construirMapaDelDia({
  cecop, dia, hermandades, live, seleccion,
}: {
  cecop: CecopDia | null;
  dia: DiaSS | null;
  hermandades: JartinetHermandad[];
  live: CecopDiaLive | null;
  /** Hermandad resaltada: las demás se dibujan tenues. */
  seleccion?: string | null;
}): MapaDelDia {
  const calib = (cecop?.map?.calib || []) as Calib[];
  const detalles = detallesDelDia(dia);
  const entradas = live?.entries || [];
  const rutas: ProcesionRuta[] = [];
  const entries: CecopLiveEntry[] = [];

  // AJUSTE FINO del encaje (ver `CecopDia.mapAjuste`): los puntos del programa
  // están en % del mapa de CIUDAD y aquí se pintan sobre el mapa del SERVIDOR.
  // Si no son el mismo fichero, todos los trazados salen corridos siempre en la
  // misma dirección; esto lo corrige antes de convertirlos a coordenadas MC.
  const dx0 = Number(cecop?.mapAjuste?.dx) || 0;
  const dy0 = Number(cecop?.mapAjuste?.dy) || 0;
  /**
   * Corre los puntos de un recorrido: el ajuste GENERAL del día más el PROPIO de esa
   * cofradía (que se pone arrastrando su recorrido sobre el mapa). Los dos van en % de
   * la imagen y se suman.
   */
  const ajustar = (pts: { x: number; y: number }[] | undefined, h?: CecopDiaHermandad) => {
    const dx = dx0 + (Number(h?.ajuste?.dx) || 0);
    const dy = dy0 + (Number(h?.ajuste?.dy) || 0);
    if (!pts || (dx === 0 && dy === 0)) return pts;
    return pts.map((p) => ({ x: p.x + dx, y: p.y + dy }));
  };

  (cecop?.hermandades || []).forEach((h) => {
    const detalle = detalles.find((d) => d.hermandadId === h.hermandadId);
    const hermandad = hermandades.find((x) => x.id === h.hermandadId);
    const color = h.color || detalle?.recorrido?.color || hermandad?.colorPrimario || COLOR_POR_DEFECTO;
    const ida = recorridoAMc(ajustar(detalle?.recorrido?.ida, h), calib);
    const vuelta = recorridoAMc(ajustar(detalle?.recorrido?.vuelta, h), calib);
    const iglesia = detalle?.recorrido?.iglesia
      ? recorridoAMc(ajustar([detalle.recorrido.iglesia], h), calib)[0] || null : null;
    const estacion = detalle?.recorrido?.estacion
      ? recorridoAMc(ajustar([detalle.recorrido.estacion], h), calib)[0] || null : null;

    // Puestos de esta hermandad, ordenados como los espera el dibujo del
    // cortejo (cruz de guía delante, pasos por número detrás).
    const suyas = entradas.filter((e) => e.hermandadId === h.hermandadId);
    const marcadores: CecopLiveEntry[] = suyas.map((e: CecopDiaLiveEntry) => ({
      role: e.role,
      index: e.index,
      label: `${h.nombre} · ${e.label}`,
      uuid: '',
      name: h.nombre,
      x: e.x,
      z: e.z,
      online: e.online,
      iconUrl: hermandad?.escudoUrl || undefined,
      ringColor: color,
      // Para que el panel público sepa de quién es el marcador que se apunta con
      // el ratón y pueda sacar SU cuadro de horarios al lado.
      hermandadId: h.hermandadId,
    }));
    entries.push(...marcadores);

    // La LÍNEA DEL CORTEJO (cruz de guía → pasos, en otro color) se quitó por
    // completo el 2026-09-02 a petición del usuario. No reimplementar.
    rutas.push({
      id: h.hermandadId,
      color,
      ida,
      vuelta,
      iglesia,
      estacion,
      atenuada: !!seleccion && seleccion !== h.hermandadId,
    });
  });

  return { rutas, entries };
}

/**
 * El día de CECOP activo (si lo hay) y su seguimiento en vivo, más el día del
 * programa del que salen los recorridos. Se usa en la portada pública, así que
 * todo lo que lee es de **lectura pública**: `cecop_dias` con `activo == true`
 * (la consulta DEBE filtrar por ese campo o la regla tumba el `list` entero),
 * `cecop_dia_live` y `dias_ss`.
 */
export function useCecopDiaActivo() {
  const [cecop, setCecop] = useState<CecopDia | null>(null);
  const [live, setLive] = useState<CecopDiaLive | null>(null);
  const [dia, setDia] = useState<DiaSS | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'cecop_dias'), where('activo', '==', true), limit(1));
    const u = onSnapshot(
      q,
      (snap) => setCecop(snap.empty ? null : ({ id: snap.docs[0].id, ...(snap.docs[0].data() as any) })),
      () => setCecop(null),
    );
    return () => u();
  }, []);

  useEffect(() => {
    if (!cecop?.id) { setLive(null); setDia(null); return; }
    const uLive = onSnapshot(
      doc(db, 'cecop_dia_live', cecop.id),
      (snap) => setLive(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null),
      () => setLive(null),
    );
    const uDia = onSnapshot(
      doc(db, 'dias_ss', cecop.id),
      (snap) => setDia(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null),
      () => setDia(null),
    );
    return () => { uLive(); uDia(); };
  }, [cecop?.id]);

  return { cecop, live, dia };
}
